import {
    Component,
    OnInit,
    ChangeDetectionStrategy,
    Input,
    Inject,
    ElementRef,
    HostBinding,
    Output,
    EventEmitter,
    HostListener,
    ViewChild,
    ViewChildren,
    QueryList,
    AfterViewInit,
    ContentChildren,
} from '@angular/core';
import { IccAppearance } from '../../enums/appearance';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { InteractiveState } from '../../enums/interactive-state';
import { IconComponent } from '../icon/icon.component';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'button[iccButton], a[iccButton], button[iccIconButton], a[iccIconButton]',
    templateUrl: './button.component.html',
    styleUrls: ['./button.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ButtonComponent implements AfterViewInit {

    private _prefixIcon: string | null = null;

    @Input()
    @HostBinding('attr.data-appearance')
    appearance: IccAppearance | string = IccAppearance.Primary;
    private _disabled = false;

    @Input()
    @HostBinding('class.icc-disabled')
    get disabled() {
        return this._disabled;
    }
    set disabled(value: any) {
        this._disabled = coerceBooleanProperty(value);
    }

    @HostBinding('attr.disabled')
    get nativeDisabled(): '' | null {
        return this.disabled ? '' : null;
    }

    hovered = false;
    pressed = false;
    focusVisible = false;

    get focused(): boolean {
        return true;
    }

    @Input()
    pseudoHovered: boolean | null = null;

    @Input()
    pseudoPressed: boolean | null = null;

    @Input()
    pseudoFocused: boolean | null = null;

    /**
     * Determines if component is focusable with keyboard.
     */
    @Input()
    focusable = true;

    @Input()
    nativeId = '';

    @Input()
    get prefixIcon(): string | null {
        return this._prefixIcon;
    }
    set prefixIcon(prefixIcon: string | null) {
        this._prefixIcon = prefixIcon;
    }

    @Input()
    @HostBinding('class.icc-small-icon-button')
    small = false;

    @Output()
    readonly focusedChange = new EventEmitter<boolean>();

    @Output()
    readonly pressedChange = new EventEmitter<boolean>();

    @Output()
    readonly hoveredChange = new EventEmitter<boolean>();

    @Output()
    readonly focusVisibleChange = new EventEmitter<boolean>();

    @ContentChildren(IconComponent, { read: ElementRef }) icons!: QueryList<ElementRef<HTMLElement>>;

    private isIconButton = false;

    constructor(@Inject(ElementRef) public readonly elementRef: ElementRef<HTMLElement>) {
        this.handleIconButton();
    }

    ngAfterViewInit() {

        this.handleClasses();

    }

    get computedDisabled(): boolean {
        return this.disabled;
    }
    @HostBinding('class._pressed')
    get computedPressed(): boolean {
        return !this.computedDisabled && (this.pseudoPressed ?? this.pressed);
    }

    get computedFocusable(): boolean {
        return !this.computedDisabled && (this.focusable || this.focused);
    }

    @HostBinding('class._focused')
    get computedFocused(): boolean {
        return !this.computedDisabled && (this.pseudoFocused ?? this.focused);
    }

    @HostBinding('class._focus-visible')
    get computedFocusVisible(): boolean {
        return !this.computedDisabled && (this.pseudoFocused ?? this.focusVisible);
    }

    @HostBinding('tabIndex')
    get tabIndex(): number {
        return this.focusable ? 0 : -1;
    }

    @HostListener('focusin', ['true'])
    @HostListener('focusout', ['false'])
    onFocused(focused: boolean) {
        this.updateFocused(focused);
    }

    @HostBinding('attr.data-state')
    get interactiveState(): InteractiveState | null {
        if (this.disabled) {
            return InteractiveState.Disabled;
        }

        if (this.pressed) {
            return InteractiveState.Pressed;
        }

        if (this.hovered) {
            return InteractiveState.Hovered;
        }

        return null;
    }

    protected updateHovered(hovered: boolean) {
        if (this.hovered === hovered) {
            return;
        }

        this.hovered = hovered;
        this.hoveredChange.emit(hovered);
    }

    protected updatePressed(pressed: boolean) {
        if (this.pressed === pressed) {
            return;
        }

        this.pressed = pressed;
        this.pressedChange.emit(pressed);
    }

    protected updateFocused(focused: boolean) {
        this.focusedChange.emit(focused);
    }

    protected updateFocusVisible(focusVisible: boolean) {
        if (this.focusVisible === focusVisible) {
            return;
        }

        this.focusVisible = focusVisible;
        this.focusVisibleChange.emit(focusVisible);
    }

    protected handleIconButton(){
        if (this.elementRef.nativeElement.hasAttribute('iccIconButton')) {
            (this.elementRef.nativeElement as HTMLElement).classList.add('icc-icon-button');
            this.isIconButton = true;
        } else {
            (this.elementRef.nativeElement as HTMLElement).classList.add('icc-button');
        }
    }

    protected handleClasses(){

        if (this.icons?.length > 0 && !this.isIconButton) {

            const textNodeIndex = Array.from<ChildNode>(this.icons.first.nativeElement.parentNode?.childNodes || []).findIndex((node) => node.nodeName === '#text');
            if (textNodeIndex === -1) {
                return;
            }
            this.icons.map(el  => {
                if(
                    Array.from<ChildNode>(el.nativeElement.parentNode?.childNodes || []).findIndex(node => node === el.nativeElement) < textNodeIndex
                ){
                    el.nativeElement.classList.add('icc-button-icon-before');
                    el.nativeElement.parentElement?.classList.add('icc-button-content-icon-before');
                }else if(
                    Array.from<ChildNode>(el.nativeElement.parentNode?.childNodes || []).findIndex(node => node === el.nativeElement) > textNodeIndex
                ){
                    el.nativeElement.classList.add('icc-button-icon-after');
                    el.nativeElement.parentElement?.classList.add('icc-button-content-icon-after');
                }
            });

        }else if (this.icons?.length && this.isIconButton) {

            this.icons.map(el  => {
                el.nativeElement.parentElement?.classList.add('icc-icon-button');
            });
        }

    }
}
